// Single home for translating a warehouse document line (WZ / PZ) into the
// shared "Przelicznik jednostek" editor value and back.
//
// The two models differ on purpose:
//
//   line     : quantity + unit (the operator-selected input for this line)
//              + pricingQuantity/pricingUnit (the price and line total use it)
//              + conversionUnit (optional m²/m³ presentation hint)
//   editor   : physicalQuantity/physicalUnit + pricingQuantity/pricingUnit
//
// Both quantity pairs are persisted snapshots. Dimensions are used only to
// calculate a proposed conversion; once the operator accepts it, the commercial
// pair stays stable even if the product dimensions change later.
import {
  AllocationUnit,
  ConversionSource,
  WZConversionUnit,
  computeConversionValue,
  normalizeConversionUnit,
} from '../../utils/dimensionConversion';
import { calculateLineValue, roundUnitPrice } from '../../utils/money';
import {
  CommercialQuantityValue,
  MAX_CUSTOMER_PRICING_QUANTITY,
  asQuantityUnit,
  resolveStoredQuantitySet,
  scaleQuantitySetToAmount,
  getPersistedQuantityForUnit,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetRatio,
  roundCommercialQuantity,
} from '../../utils/commercialQuantity';
import type { QuantityUnit } from '../../utils/commercialQuantity';
import { formatUnit, normalizeUnitCode } from '../../utils/units';
import { WAREHOUSE_QUANTITY_PRECISION } from '../../utils/warehouseQuantity';
import { localeForLanguage } from '../../utils/numberFormatting';

export interface WarehouseQuantityLine extends ConversionSource {
  productVariantId?: string | null;
  quantity?: number | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit?: string | null;
  conversionUnit?: WZConversionUnit | null;
  /** Persisted commercial quantity used by unitPrice (PZ purchase / WZ sale). */
  pricingQuantity?: number | null;
  /** Persisted commercial unit used by unitPrice. */
  pricingUnit?: string | null;
}

export interface WarehouseQuantityPatch {
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  conversionQuantity?: number;
  conversionUnit: AllocationUnit | null;
  pricingQuantity?: number;
  pricingUnit?: QuantityUnit;
  unit: string;
}

type WarehouseQuantitySetPatch = Partial<Pick<
  WarehouseQuantityPatch,
  'quantityPcs' | 'quantityM2' | 'quantityM3'
>>;

// Single bound for the commercial quantity accepted by all three warehouse call
// sites (WZ create, WZ detail, PZ items).
export const WAREHOUSE_CONVERTED_QUANTITY_MAX = MAX_CUSTOMER_PRICING_QUANTITY;

/**
 * The unit a line's quantity is denominated in, as an editor-compatible code.
 *
 * This reads the LINE's own unit, which is what the quantity means today. Lines
 * carry either a warehouse code ('pcs', 'm2', …) or the raw `ProductUnit` enum
 * ('PIECE', 'SQUARE_METER') copied from legacy rows; normalizeUnitCode
 * collapses both. Units the dimensional model does not cover (kg, m, mb, l)
 * keep their own code and simply get no conversion option.
 */
export function getWarehouseLineUnit(line: WarehouseQuantityLine): QuantityUnit {
  return asQuantityUnit(normalizeUnitCode(line.unit) ?? 'pcs');
}

/** True when the line's selected unit supports dimensional conversion. */
export function supportsWarehouseConversion(line: WarehouseQuantityLine): boolean {
  const code = normalizeUnitCode(line.unit) ?? 'pcs';
  return code === 'pcs' || code === 'm2' || code === 'm3';
}

/** Line -> editor value. */
export function buildWarehouseQuantityValue(line: WarehouseQuantityLine): CommercialQuantityValue {
  const physicalUnit = getWarehouseLineUnit(line);
  const physicalQuantity = Number(line.quantity) || 0;
  const conversionUnit = normalizeConversionUnit(line.conversionUnit);
  const persistedPricingUnit = line.pricingUnit ? asQuantityUnit(line.pricingUnit, physicalUnit) : null;
  const persistedPricingQuantity = Number(line.pricingQuantity);
  const persistedConversion = getPersistedQuantityForUnit(
    line as Record<string, unknown>,
    conversionUnit,
  );
  const hasStoredQuantitySet = hasPersistedQuantitySet(line as Record<string, unknown>);
  const storedQuantityFields = hasStoredQuantitySet
    ? {
      quantityPcs: line.quantityPcs ?? null,
      quantityM2: line.quantityM2 ?? null,
      quantityM3: line.quantityM3 ?? null,
    }
    : {};
  const converted = conversionUnit && conversionUnit !== physicalUnit
    ? hasStoredQuantitySet
      ? persistedConversion
      : computeConversionValue(
          conversionUnit,
          { ...line, unit: physicalUnit as AllocationUnit },
          physicalQuantity,
        )
    : null;

  if (persistedPricingUnit && Number.isFinite(persistedPricingQuantity) && persistedPricingQuantity > 0) {
    return {
      physicalQuantity,
      physicalUnit,
      pricingQuantity: persistedPricingQuantity,
      pricingUnit: persistedPricingUnit,
      ...storedQuantityFields,
      ...(conversionUnit && conversionUnit !== physicalUnit
        ? {
          conversionUnit,
          ...(converted === null ? {} : { conversionQuantity: roundCommercialQuantity(converted) }),
        }
        : {}),
    };
  }

  if (!conversionUnit || conversionUnit === physicalUnit) {
    return {
      physicalQuantity,
      physicalUnit,
      pricingQuantity: physicalQuantity,
      pricingUnit: physicalUnit,
      ...storedQuantityFields,
    };
  }

  return {
    physicalQuantity,
    physicalUnit,
    // A line whose dimensions cannot produce the conversion still keeps the
    // operator's chosen unit — the form shows "brak wymiarów" rather than
    // silently dropping the choice.
    pricingQuantity: converted === null ? 0 : roundCommercialQuantity(converted),
    pricingUnit: conversionUnit,
    ...storedQuantityFields,
  };
}

/**
 * One warehouse quantity with its unit, Polish-formatted.
 *
 * Single home for "a number next to a unit" on a warehouse line. Interpolating
 * the raw number renders 3.75 with an English dot right beside a Polish `m²`,
 * while the totals row and the PDF next to it print 3,75 — two decimal
 * separators on one screen.
 */
export function formatWarehouseQuantity(
  quantity: number | null | undefined,
  unit: string | null | undefined,
  language = 'pl',
): string {
  const amount = (Number(quantity) || 0).toLocaleString(localeForLanguage(language), {
    maximumFractionDigits: WAREHOUSE_QUANTITY_PRECISION,
  });

  return `${amount} ${formatUnit(unit, language)}`;
}

/**
 * "RAZEM" for a document's quantity column.
 *
 * A document may mix units (a pieces line next to an m² line) and those cannot
 * collapse into one number, so each unit is totalled separately. The frontend
 * twin of buildWarehouseQuantityTotalLabel in backend documentPdfService.js —
 * the screen and the PDF must agree.
 */
export function formatWarehouseQuantityTotals(
  lines: WarehouseQuantityLine[],
  language = 'pl',
): string {
  const totalsByUnit = new Map<string, number>();

  lines.forEach((line) => {
    const unit = normalizeUnitCode(line.unit) ?? 'pcs';
    totalsByUnit.set(unit, (totalsByUnit.get(unit) || 0) + (Number(line.quantity) || 0));
  });

  return Array.from(totalsByUnit.entries())
    .filter(([, total]) => total)
    .map(([unit, total]) => formatWarehouseQuantity(total, unit, language))
    .join(', ');
}

/**
 * Editor value -> the two fields the line actually persists.
 *
 * The pricing pair is persisted because it is a commercial snapshot. It may
 * originate from an invoice/order and must not change when catalog dimensions
 * are edited later.
 */
export function buildWarehouseQuantityPatch(value: CommercialQuantityValue): WarehouseQuantityPatch {
  const requestedConversionUnit = value.conversionUnit === undefined
    ? (value.pricingUnit === value.physicalUnit ? null : normalizeConversionUnit(value.pricingUnit))
    : value.conversionUnit;
  const conversionUnit = !requestedConversionUnit || requestedConversionUnit === value.physicalUnit
    ? null
    : requestedConversionUnit;

  return {
    quantity: roundCommercialQuantity(value.physicalQuantity),
    ...(value.quantityPcs === undefined
      ? {}
      : { quantityPcs: value.quantityPcs === null ? null : roundCommercialQuantity(value.quantityPcs) }),
    ...(value.quantityM2 === undefined
      ? {}
      : { quantityM2: value.quantityM2 === null ? null : roundCommercialQuantity(value.quantityM2) }),
    ...(value.quantityM3 === undefined
      ? {}
      : { quantityM3: value.quantityM3 === null ? null : roundCommercialQuantity(value.quantityM3) }),
    unit: value.physicalUnit,
    conversionUnit,
    ...(value.conversionQuantity == null
      ? {}
      : { conversionQuantity: roundCommercialQuantity(value.conversionQuantity) }),
    pricingQuantity: roundCommercialQuantity(value.pricingQuantity),
    pricingUnit: value.pricingUnit,
  };
}

/**
 * Presence-aware atomic-set slice for warehouse form state. New draft values
 * may not have a set yet, while a persisted line can deliberately contain a
 * null component. Callers must therefore distinguish "not supplied" from
 * "replace this component with null" instead of using `??` fallbacks.
 */
export function extractWarehouseQuantitySetPatch(
  patch: WarehouseQuantityPatch,
): WarehouseQuantitySetPatch {
  return {
    ...(patch.quantityPcs === undefined ? {} : { quantityPcs: patch.quantityPcs }),
    ...(patch.quantityM2 === undefined ? {} : { quantityM2: patch.quantityM2 }),
    ...(patch.quantityM3 === undefined ? {} : { quantityM3: patch.quantityM3 }),
  };
}

/**
 * Re-express a received/damaged subquantity when the same parent amount is
 * shown in another unit. Example: a 100 szt. line with 50 szt. received,
 * re-expressed as 3 m³, must carry 1.5 m³ received instead of relabelling 50
 * as 50 m³.
 */
export function reexpressWarehouseSubquantity(
  quantity: number | null | undefined,
  previousTotal: number,
  nextTotal: number,
): number | null {
  if (quantity === null || quantity === undefined) return null;
  const numericQuantity = Number(quantity);
  const numericPreviousTotal = Number(previousTotal);
  const numericNextTotal = Number(nextTotal);
  if (
    !Number.isFinite(numericQuantity)
    || !Number.isFinite(numericPreviousTotal)
    || numericPreviousTotal <= 0
    || !Number.isFinite(numericNextTotal)
  ) return null;

  return roundCommercialQuantity(numericQuantity * numericNextTotal / numericPreviousTotal);
}

/**
 * Rebase a unit price when the operator changes physical quantity and/or the
 * commercial basis. The economic value of the represented quantity stays stable:
 * changing 70 szt. / 208,376 m² into 40 szt. / 119,072 m² keeps the same
 * price per m² and scales the line total; switching only the basis keeps the
 * line total and shows the equivalent price in the newly selected unit.
 */
export function rebaseWarehouseUnitPrice(
  current: Pick<WarehouseQuantityLine,
    'quantity' | 'unit' | 'pricingQuantity' | 'pricingUnit'
    | 'width' | 'height' | 'length' | 'thickness'
    | 'quantityPcs' | 'quantityM2' | 'quantityM3'
  > & { unitPrice?: number | null },
  next: Pick<WarehouseQuantityPatch, 'quantity' | 'pricingQuantity'>
    & Partial<Pick<WarehouseQuantityPatch, 'unit' | 'pricingUnit' | 'quantityPcs' | 'quantityM2' | 'quantityM3'>>
    & ConversionSource,
): { unitPrice: number; totalValue: number } {
  const currentPhysicalQuantity = Number(current.quantity) || 0;
  const currentPricingQuantity = Number(current.pricingQuantity) > 0
    ? Number(current.pricingQuantity)
    : currentPhysicalQuantity;
  const currentUnitPrice = Number(current.unitPrice) || 0;
  const currentLineValue = currentPricingQuantity * currentUnitPrice;
  const nextPhysicalQuantity = Number(next.quantity) || 0;
  const nextPricingQuantity = Number(next.pricingQuantity) || nextPhysicalQuantity;
  const currentUnit = normalizeUnitCode(current.unit);
  const nextUnit = normalizeUnitCode(next.unit);
  const currentPricingUnit = normalizeUnitCode(current.pricingUnit);
  const nextPricingUnit = normalizeUnitCode(next.pricingUnit);
  // Money follows the accepted commercial pair. A rounded physical view (for
  // example m³) must not change a half-order's price compared with its pcs view.
  let representedQuantityRatio: number | null = currentPricingUnit
    && currentPricingUnit === nextPricingUnit && currentPricingQuantity > 0
      ? nextPricingQuantity / currentPricingQuantity
      : null;

  if (representedQuantityRatio === null && isAllocationUnit(currentUnit) && isAllocationUnit(nextUnit)) {
    const currentSet = resolveStoredQuantitySet(current);
    const nextSet = hasPersistedQuantitySet({ ...next })
      ? scaleQuantitySetToAmount(resolveStoredQuantitySet({ ...next }), nextPhysicalQuantity, nextUnit)
      : hasPersistedQuantitySet(current)
        ? scaleQuantitySetToAmount(currentSet, nextPhysicalQuantity, nextUnit)
        : resolveStoredQuantitySet({ ...current, ...next });
    representedQuantityRatio = nextSet ? quantitySetRatio(nextSet, currentSet).ratio : null;
  }

  if (representedQuantityRatio === null && currentPhysicalQuantity > 0
    && currentUnit === nextUnit) {
    representedQuantityRatio = nextPhysicalQuantity / currentPhysicalQuantity;
  }

  const nextLineValue = currentPhysicalQuantity > 0
    ? currentLineValue * (representedQuantityRatio ?? 1)
    : nextPricingQuantity * currentUnitPrice;
  const unitPrice = nextPricingQuantity > 0 ? nextLineValue / nextPricingQuantity : currentUnitPrice;
  const persistedUnitPrice = roundUnitPrice(unitPrice);

  return {
    // Migration 121 stores commercial unit prices at four decimal places.
    // Round here too so the displayed rate and the backend-derived total use
    // the same persisted value.
    unitPrice: persistedUnitPrice,
    totalValue: calculateLineValue(nextPricingQuantity, persistedUnitPrice),
  };
}
